import { Backdrop, CircularProgress } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import AppContext from "./components/context/context";
import Layout from "./components/layout";
import { roleSidemenuMapService } from "./utils/services/role-sidemenu-map.service";
import { userSidemenuPreferenceService } from "./utils/services/user-sidemenu-preference.service";
import { clientService } from "./utils/services/clientService";
import { ForecastProvider } from "./context/forecastContext";
import { LostSalesProvider } from "./context/lostSalesContext";
const ProtectedRoute = () => {
  const { isAuthenticated, setCurrentMenu, setCurrentSubMenu } =
    useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [passwordSetupRedirect, setPasswordSetupRedirect] = useState(null);
  const location = useLocation();
  const path = localStorage.getItem("path");

  useEffect(() => {
    if (isAuthenticated) {
      fetchSidemenu();
      checkPasswordPending();
    } else if (isAuthenticated === false) {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Same account-setup gate the Shopify-onboarding flow relies on
  // (client-onboard.class.js leaves password_generation_pending: true until
  // the admin sets a real password on /onboard-login) — checked here too as
  // a safety net so any authenticated session with that flag still set gets
  // routed to finish setup, regardless of which flow created the user.
  const checkPasswordPending = async () => {
    try {
      const clientId = localStorage.getItem("client_id");
      if (!clientId) return;
      const status = await clientService.clientPasswordCheck(clientId);
      if (status?.pending) {
        const user = (() => {
          try {
            return JSON.parse(localStorage.getItem("user"));
          } catch {
            return null;
          }
        })();
        const params = new URLSearchParams();
        if (user?.email) params.set("email", user.email);
        if (status.validate_key) params.set("validate_key", status.validate_key);
        setPasswordSetupRedirect(`/onboard-login?${params.toString()}`);
      }
    } catch (error) {
      // Non-fatal: don't block navigation if the check itself fails
      console.error("Error checking password setup status:", error);
    }
  };

  const fetchSidemenu = async () => {
    try {
      const data = await roleSidemenuMapService.fetchByLoggedInUser();
      let orderedData = data;
      const userId = (() => {
        try {
          return JSON.parse(localStorage.getItem("user"))?._id;
        } catch {
          return null;
        }
      })();
      if (userId) {
        try {
          const pref = await userSidemenuPreferenceService.fetchByUser(userId);
          if (pref?.preferences?.length) {
            orderedData = applyPreferences(data, pref.preferences);
          }
        } catch (e) {
          // preference fetch failure is non-fatal; use default ordering
        }
      }
      localStorage.setItem("sidemenuItems", JSON.stringify(orderedData));
      window.dispatchEvent(new Event("sidemenu-updated"));
    } catch (error) {
      console.error("Error fetching sidemenu:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyPreferences = (data, preferences) => {
    const prefMap = {};
    preferences.forEach((p) => {
      prefMap[p.pkey] = p;
    });
    const sorted = [...data].sort((a, b) => {
      const aOrder = prefMap[a.pkey]?.order ?? a.order;
      const bOrder = prefMap[b.pkey]?.order ?? b.order;
      return aOrder - bOrder;
    });
    return sorted.map((item) => {
      const pref = prefMap[item.pkey];
      const mapped = { ...item, title: pref?.label ?? item.title };
      if (item.subNav?.length) {
        return {
          ...mapped,
          subNav: [...item.subNav]
            .sort((a, b) => {
              const aOrder = prefMap[a.pkey]?.order ?? a.order;
              const bOrder = prefMap[b.pkey]?.order ?? b.order;
              return aOrder - bOrder;
            })
            .map((child) => {
              const childPref = prefMap[child.pkey];
              return { ...child, title: childPref?.label ?? child.title };
            }),
        };
      }
      return mapped;
    });
  };

  useEffect(() => {
    if (!location.pathname.includes(path)) {
      localStorage.setItem("path", location.pathname);
      const sidebarData = JSON.parse(
        localStorage.getItem("sidemenuItems") || `[]`,
      );

      const foundMenu = sidebarData.find(
        (item) =>
          item.path === location.pathname ||
          item.subNav?.some((subItem) => subItem.path === location.pathname),
      );
      if (foundMenu) {
        setCurrentSubMenu(
          foundMenu.subNav?.find((x) => x.path === location.pathname) || {
            title: "",
          },
        );
      }

      setCurrentMenu(foundMenu || { title: "" });
      localStorage.getItem("parent_tab");
    }
  }, [location.pathname]);
  // Fallback: prevent infinite loading if auth state never resolves
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 10000);
    return () => clearTimeout(timer);
  }, []);
  if (loading) {
    return (
      <Backdrop
        open={true}
        style={{
          zIndex: 999,
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100vw",
            height: "100vh",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="primary" />
          <p
            style={{
              marginTop: 10,
              animation: "loadingAnim 1s infinite alternate",
            }}
          >
            Loading...
          </p>
          {/* Added animation */}
        </div>
      </Backdrop>
    );
  }

  if (isAuthenticated && passwordSetupRedirect) {
    return <Navigate replace={true} to={passwordSetupRedirect} />;
  }

  return isAuthenticated ? (
    <div>
      <ForecastProvider>
        <LostSalesProvider>
          <Layout>
            <Outlet />
          </Layout>
        </LostSalesProvider>
      </ForecastProvider>
    </div>
  ) : (
    <Navigate replace={true} to="/login" />
  );
};

export default ProtectedRoute;
